import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Coins, ArrowDownToLine, ArrowUpFromLine, RotateCcw, Dice5, Trophy, Wallet, Repeat, Flame, ShieldAlert, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { BankAccount } from '@/lib/types';
import {
  getMyBankAccounts, casinoGetWallet, casinoDeposit, casinoWithdraw, casinoSpinRoulette, casinoSpinMegaFire,
  type RouletteBet, type FireBonus,
} from '@/lib/actions';
import { FireBonusOverlay } from '@/components/jartibet/FireBonusOverlay';
import { MegaFireWheel, FireNumbersPanel } from '@/components/jartibet/MegaFireWheel';
import { SportsBetting } from '@/components/jartibet/SportsBetting';

// Interruptor de la Mega Fire Blaze (debe coincidir con MEGAFIRE_ENABLED en functions/index.js).
// En false la mesa sale bloqueada, no se puede seleccionar y el backend rechaza las jugadas.
const MEGAFIRE_ENABLED = false;

// ── Constantes de ruleta europea ──
const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const WHEEL_ORDER = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];
const STEP = 360 / 37;
const CHIPS = [1, 5, 10, 25, 100, 500, 1000];
const CHIP_COLORS: Record<number, string> = { 1: '#d4d4d8', 5: '#dc2626', 10: '#f97316', 25: '#16a34a', 100: '#2563eb', 500: '#9333ea', 1000: '#eab308' };
const INSIDE_KINDS = ['split', 'street', 'corner', 'line'];

function numColor(n: number): 'green' | 'red' | 'black' {
  return n === 0 ? 'green' : RED.has(n) ? 'red' : 'black';
}
function chipColorFor(v: number) {
  if (v >= 1000) return '#eab308';
  if (v >= 500) return '#9333ea';
  if (v >= 100) return '#2563eb';
  if (v >= 25) return '#16a34a';
  if (v >= 10) return '#f97316';
  if (v >= 5) return '#dc2626';
  return '#d4d4d8';
}

// ── Ficha de casino realista (CSS puro) ──
function PokerChip({ value, size = 44, color, label }: { value?: number; size?: number; color: string; label?: string }) {
  const edge = '#ffffff';
  const txtColor = color === '#d4d4d8' ? '#27272a' : '#ffffff';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <div className="absolute inset-0 rounded-full" style={{
        background: `repeating-conic-gradient(${edge} 0deg 11deg, ${color} 11deg 30deg)`,
        boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.35)',
      }} />
      <div className="absolute rounded-full" style={{
        inset: size * 0.13,
        background: `radial-gradient(circle at 36% 28%, rgba(255,255,255,0.55), ${color} 62%)`,
        boxShadow: `inset 0 0 0 ${Math.max(1, size * 0.045)}px rgba(255,255,255,0.55), inset 0 -2px 6px rgba(0,0,0,0.35)`,
      }} />
      <div className="absolute grid place-items-center rounded-full" style={{
        inset: size * 0.27,
        background: `radial-gradient(circle at 40% 30%, rgba(255,255,255,0.3), ${color})`,
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.2)',
      }}>
        <span style={{ fontSize: size * 0.3, color: txtColor, fontWeight: 900, textShadow: '0 1px 1px rgba(0,0,0,0.45)' }}>{label ?? value}</span>
      </div>
    </div>
  );
}

// ── Rueda visual ──
function RouletteWheel({ rotation, ballRotation, spinning, result }: { rotation: number; ballRotation: number; spinning: boolean; result: number | null }) {
  const cx = 160, cy = 160, R = 158;
  const rSector = 130, rText = 113, rTrack = 142, rBall = 142;
  const pointAt = (a: number, rad: number) => [cx + rad * Math.sin((a * Math.PI) / 180), cy - rad * Math.cos((a * Math.PI) / 180)];

  return (
    <div className="relative mx-auto" style={{ width: 320, height: 320 }}>
      <div className="absolute inset-0 rounded-full bg-amber-500/20 blur-2xl" />
      <div className="absolute left-1/2 top-[-10px] z-30 -translate-x-1/2">
        <div className="h-0 w-0 border-x-[10px] border-t-[18px] border-x-transparent border-t-amber-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
      </div>
      <svg width={320} height={320} viewBox="0 0 320 320" className="absolute inset-0">
        <defs>
          <radialGradient id="goldRim" cx="50%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#fde68a" /><stop offset="45%" stopColor="#d4a93f" />
            <stop offset="75%" stopColor="#8a6418" /><stop offset="100%" stopColor="#3d2c0a" />
          </radialGradient>
          <radialGradient id="hub" cx="42%" cy="32%" r="75%">
            <stop offset="0%" stopColor="#fff4cc" /><stop offset="50%" stopColor="#caa253" /><stop offset="100%" stopColor="#5b4416" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={R} fill="url(#goldRim)" />
        <circle cx={cx} cy={cy} r={R - 14} fill="#1a1207" />
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i * 360) / 24; const [bx, by] = pointAt(a, R - 7);
          return <circle key={i} cx={bx} cy={by} r={2.4} fill="#fff3c4" opacity={0.85} />;
        })}
      </svg>
      <motion.svg width={320} height={320} viewBox="0 0 320 320" animate={{ rotate: rotation }}
        transition={{ duration: spinning ? 5 : 0, ease: [0.16, 0.74, 0.1, 1] }} className="absolute inset-0" style={{ transformOrigin: '50% 50%' }}>
        <circle cx={cx} cy={cy} r={rTrack} fill="#241a0c" stroke="#caa253" strokeWidth={1.5} />
        {WHEEL_ORDER.map((n, i) => {
          const a0 = i * STEP, a1 = (i + 1) * STEP;
          const [x0, y0] = pointAt(a0, rSector); const [x1, y1] = pointAt(a1, rSector); const [tx, ty] = pointAt(a0 + STEP / 2, rText);
          const fill = numColor(n) === 'green' ? '#0a8f3c' : numColor(n) === 'red' ? '#c81e1e' : '#0f0f12';
          return (
            <g key={i}>
              <path d={`M ${cx} ${cy} L ${x0} ${y0} A ${rSector} ${rSector} 0 0 1 ${x1} ${y1} Z`} fill={fill} stroke="#caa253" strokeWidth={0.7} />
              <text x={tx} y={ty} fill="#fff" fontSize={10} fontWeight={800} textAnchor="middle" dominantBaseline="middle" transform={`rotate(${a0 + STEP / 2}, ${tx}, ${ty})`}>{n}</text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={62} fill="url(#hub)" stroke="#3d2c0a" strokeWidth={1.5} />
        <circle cx={cx} cy={cy} r={44} fill="#1a1207" />
        <circle cx={cx} cy={cy} r={26} fill="url(#hub)" />
        {[0, 45, 90, 135].map((a) => {
          const [px1, py1] = pointAt(a, 60); const [px2, py2] = pointAt(a + 180, 60);
          return <line key={a} x1={px1} y1={py1} x2={px2} y2={py2} stroke="#caa253" strokeWidth={3} strokeLinecap="round" opacity={0.5} />;
        })}
        <circle cx={cx} cy={cy} r={9} fill="#fff4cc" />
      </motion.svg>
      <motion.div className="absolute inset-0 z-20" animate={{ rotate: ballRotation }} transition={{ duration: spinning ? 5 : 0, ease: [0.16, 0.74, 0.1, 1] }} style={{ transformOrigin: '50% 50%' }}>
        <div className="absolute left-1/2 -translate-x-1/2 rounded-full" style={{
          top: cx - rBall - 4, width: 13, height: 13,
          background: 'radial-gradient(circle at 35% 30%, #ffffff, #c7c7c7 70%, #8a8a8a)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.6), inset 0 -1px 2px rgba(0,0,0,0.4)',
        }} />
      </motion.div>
      {result !== null && !spinning && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 16 }}
            className={cn('grid h-[72px] w-[72px] place-items-center rounded-full border-[3px] border-amber-200 text-3xl font-black text-white shadow-[0_0_24px_rgba(0,0,0,0.7)]',
              numColor(result) === 'green' ? 'bg-green-600' : numColor(result) === 'red' ? 'bg-red-600' : 'bg-zinc-900')}>{result}</motion.div>
        </div>
      )}
    </div>
  );
}

// ── Tablero de números con apuestas internas (entre números) ──
const W = 46, H = 46, X0 = 46, COLW = 46;
const BOARD_W = X0 + 12 * W + COLW; // 644
const BOARD_H = 3 * H;              // 138
const numAt = (col: number, row: number) => row === 0 ? col * 3 + 3 : row === 1 ? col * 3 + 2 : col * 3 + 1;
const idFor = (kind: string, nums: number[]) => `${kind}:${[...nums].sort((a, b) => a - b).join('-')}`;

function buildBoard() {
  const numberCells = [{ n: 0, x: 0, y: 0, w: X0, h: BOARD_H }] as { n: number; x: number; y: number; w: number; h: number }[];
  for (let n = 1; n <= 36; n++) {
    const col = Math.floor((n - 1) / 3);
    const row = n % 3 === 0 ? 0 : n % 3 === 2 ? 1 : 2;
    numberCells.push({ n, x: X0 + col * W, y: row * H, w: W, h: H });
  }
  const columnCells = [3, 2, 1].map((c, idx) => ({ c, x: X0 + 12 * W, y: idx * H, w: COLW, h: H }));

  const spots: { id: string; kind: string; numbers: number[]; x: number; y: number }[] = [];
  const add = (kind: string, numbers: number[], x: number, y: number) => spots.push({ id: idFor(kind, numbers), kind, numbers, x, y });

  // Splits horizontales (números contiguos en la misma fila)
  for (let col = 0; col < 11; col++) for (let row = 0; row < 3; row++) add('split', [numAt(col, row), numAt(col + 1, row)], X0 + (col + 1) * W, row * H + H / 2);
  // Splits verticales (contiguos en la misma columna)
  for (let col = 0; col < 12; col++) {
    add('split', [numAt(col, 0), numAt(col, 1)], X0 + col * W + W / 2, H);
    add('split', [numAt(col, 1), numAt(col, 2)], X0 + col * W + W / 2, 2 * H);
  }
  // Esquinas (4 números)
  for (let col = 0; col < 11; col++) for (let r = 0; r < 2; r++)
    add('corner', [numAt(col, r), numAt(col, r + 1), numAt(col + 1, r), numAt(col + 1, r + 1)], X0 + (col + 1) * W, (r + 1) * H);
  // Calles (3 números, borde inferior)
  for (let col = 0; col < 12; col++) add('street', [numAt(col, 0), numAt(col, 1), numAt(col, 2)], X0 + col * W + W / 2, BOARD_H);
  // Líneas / dobles calles (6 números)
  for (let col = 0; col < 11; col++) add('line', [numAt(col, 0), numAt(col, 1), numAt(col, 2), numAt(col + 1, 0), numAt(col + 1, 1), numAt(col + 1, 2)], X0 + (col + 1) * W, BOARD_H);
  // Cero: splits con 1/2/3 y tríos
  add('split', [0, 3], X0, H / 2); add('split', [0, 2], X0, 1.5 * H); add('split', [0, 1], X0, 2.5 * H);
  add('street', [0, 2, 3], X0, H); add('street', [0, 1, 2], X0, 2 * H);

  // Centros de ficha por id
  const centers: Record<string, { x: number; y: number; size: number }> = {};
  centers['straight:0'] = { x: X0 / 2, y: BOARD_H / 2, size: 30 };
  for (let n = 1; n <= 36; n++) {
    const c = numberCells.find((k) => k.n === n)!;
    centers[`straight:${n}`] = { x: c.x + W / 2, y: c.y + H / 2, size: 30 };
  }
  columnCells.forEach((cc) => { centers[`column:${cc.c}`] = { x: cc.x + COLW / 2, y: cc.y + H / 2, size: 26 }; });
  spots.forEach((s) => { centers[s.id] = { x: s.x, y: s.y, size: 24 }; });

  return { numberCells, columnCells, spots, centers };
}

function NumbersBoard({ bets, placeBet, fireNumbers = [] }: { bets: Record<string, number>; placeBet: (id: string) => void; fireNumbers?: number[] }) {
  const { numberCells, columnCells, spots, centers } = useMemo(buildBoard, []);
  const cellBg = (n: number) => n === 0 ? 'from-green-600 to-green-800 hover:from-green-500'
    : numColor(n) === 'red' ? 'from-red-600 to-red-800 hover:from-red-500' : 'from-zinc-800 to-black hover:from-zinc-700';
  const fire = new Set(fireNumbers);

  return (
    <div className="relative" style={{ width: BOARD_W, height: BOARD_H }}>
      {/* Casillas de números */}
      {numberCells.map((c) => {
        const isFire = fire.has(c.n);
        return (
        <button key={c.n} onClick={() => placeBet(`straight:${c.n}`)}
          className={cn('absolute flex items-start justify-center border bg-gradient-to-br pt-1 text-sm font-bold text-white', cellBg(c.n), c.n === 0 ? 'rounded-l-lg' : 'rounded-sm',
            isFire ? 'border-orange-300 shadow-[0_0_14px_rgba(251,146,60,0.9)] ring-2 ring-orange-400 animate-pulse' : 'border-amber-400/25')}
          style={{ left: c.x, top: c.y, width: c.w, height: c.h }}>
          {isFire && <Flame className="pointer-events-none absolute right-0.5 top-0.5 h-3 w-3 text-orange-300 drop-shadow" />}
          {c.n}
        </button>
      ); })}
      {/* Columnas 2:1 */}
      {columnCells.map((cc) => (
        <button key={cc.c} onClick={() => placeBet(`column:${cc.c}`)}
          className="absolute flex items-center justify-center rounded-sm border border-amber-400/25 bg-emerald-950/50 text-xs font-bold text-white hover:bg-emerald-900/60"
          style={{ left: cc.x, top: cc.y, width: cc.w, height: cc.h }}>2:1</button>
      ))}
      {/* Zonas de apuesta interna (entre números) */}
      {spots.map((s) => (
        <button key={s.id} onClick={() => placeBet(s.id)} title={`Apuesta a ${s.numbers.join(', ')}`}
          className="absolute z-10 grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-transparent transition-colors hover:border-amber-300 hover:bg-amber-300/30"
          style={{ left: s.x, top: s.y, width: s.kind === 'corner' ? 18 : 20, height: s.kind === 'corner' ? 18 : 20 }}>
          <span className="h-1.5 w-1.5 rounded-full bg-amber-300/0 transition-colors group-hover:bg-amber-300" />
        </button>
      ))}
      {/* Fichas colocadas */}
      {Object.entries(bets).map(([id, amount]) => {
        const c = centers[id];
        if (!c || amount <= 0) return null;
        return (
          <span key={id} className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2" style={{ left: c.x, top: c.y }}>
            <PokerChip value={amount} size={c.size} color={chipColorFor(amount)} />
          </span>
        );
      })}
    </div>
  );
}

// ── Celda externa (docenas, par/impar, color, mitades) ──
function OutsideCell({ label, color, amount, onClick, className }: {
  label: React.ReactNode; color?: 'red' | 'black' | 'none'; amount: number; onClick: () => void; className?: string;
}) {
  const bg = color === 'red' ? 'bg-gradient-to-br from-red-600 to-red-800 hover:from-red-500'
    : color === 'black' ? 'bg-gradient-to-br from-zinc-800 to-black hover:from-zinc-700' : 'bg-emerald-950/50 hover:bg-emerald-900/60';
  return (
    <button onClick={onClick} className={cn('relative flex items-center justify-center border border-amber-400/25 text-xs font-bold text-white transition-all', bg, className)}>
      <span className={cn(amount > 0 && 'opacity-40')}>{label}</span>
      {amount > 0 && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <PokerChip value={amount} size={26} color={chipColorFor(amount)} />
        </span>
      )}
    </button>
  );
}

// ── Sonido procedural (Web Audio) ──
function useCasinoAudio() {
  const ctxRef = useRef<AudioContext | null>(null);
  const getCtx = () => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  };
  const tone = (freq: number, start: number, dur: number, type: OscillatorType = 'sine', gain = 0.2) => {
    const ctx = getCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type; o.frequency.value = freq;
    o.connect(g); g.connect(ctx.destination);
    const t = ctx.currentTime + start;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.start(t); o.stop(t + dur + 0.02);
  };
  return {
    // Clics de la bola: empieza rápido y va frenando (≈5 s)
    spin: () => {
      let t = 0, gap = 0.045;
      for (let i = 0; i < 90 && t < 4.9; i++) { tone(1500, t, 0.025, 'square', 0.06); t += gap; gap *= 1.05; }
    },
    win: () => { [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, i * 0.1, 0.35, 'triangle', 0.28)); tone(1047, 0.5, 0.6, 'sine', 0.2); },
    lose: () => { tone(330, 0, 0.45, 'sawtooth', 0.22); tone(247, 0.16, 0.45, 'sawtooth', 0.22); tone(165, 0.32, 0.55, 'sawtooth', 0.2); },
    chip: () => tone(880, 0, 0.04, 'square', 0.07),
    // Llamarada al encender un número de fuego
    ignite: () => { tone(140, 0, 0.28, 'sawtooth', 0.22); tone(420, 0, 0.18, 'triangle', 0.14); tone(1100, 0.02, 0.1, 'square', 0.08); },
  };
}

// ── Pantalla de verificación de edad (18+) ──
function AgeGate({ onAccept }: { onAccept: () => void }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-6 backdrop-blur-sm">
      <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/80 via-black to-red-950/70 p-8 text-center shadow-[0_0_50px_rgba(245,158,11,0.3)]">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative">
          <div className="mx-auto mb-4 grid h-20 w-20 place-items-center rounded-2xl border-2 border-amber-400/50 bg-amber-500/15 text-amber-300"><ShieldAlert className="h-11 w-11" /></div>
          <h2 className="font-headline text-3xl font-black text-amber-200">Verificación de edad</h2>
          <p className="mt-3 text-amber-100/80">Para acceder a <b>JartiBet</b> debes ser <b>mayor de 18 años</b>.</p>
          <p className="mt-2 text-sm text-amber-100/50">JartiBet es un juego de azar de la comunidad. No se juega con dinero real, solo con jartis virtuales. Juega con responsabilidad.</p>
          <div className="mt-6 flex flex-col gap-2">
            <Button onClick={onAccept} className="h-12 bg-gradient-to-r from-amber-500 to-red-600 text-base font-black text-black hover:from-amber-400 hover:to-red-500">Soy mayor de 18 años · Entrar</Button>
            <a href="/jartinet/home" className="text-sm text-amber-100/60 underline">Soy menor — salir</a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function JartiBetPage() {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const audio = useCasinoAudio();

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [accountId, setAccountId] = useState<string>('');
  const [chips, setChips] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);

  const [bets, setBets] = useState<Record<string, number>>({});
  const [lastBets, setLastBets] = useState<Record<string, number>>({});
  const [chipValue, setChipValue] = useState(5);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [ballRotation, setBallRotation] = useState(0);
  const [result, setResult] = useState<number | null>(null);
  const [lastOutcome, setLastOutcome] = useState<{ net: number } | null>(null);
  const [history, setHistory] = useState<number[]>([]);
  const [depositOpen, setDepositOpen] = useState(false);
  const [flash, setFlash] = useState<'win' | 'lose' | null>(null);
  // Sección: ruleta o apuestas deportivas
  const [section, setSection] = useState<'ruleta' | 'apuestas'>('ruleta');
  // Mega Fire Blaze
  const [mode, setMode] = useState<'europea' | 'megafire'>('europea');
  const [fireNumbers, setFireNumbers] = useState<number[]>([]);
  const [litFire, setLitFire] = useState<number[]>([]);
  const [phase, setPhase] = useState<'idle' | 'igniting' | 'spinning'>('idle');
  const [megaEvent, setMegaEvent] = useState(false);
  const [activeBonus, setActiveBonus] = useState<{ bonus: FireBonus; betAmount: number; number: number; payout: number } | null>(null);
  // Verificación de edad (cada vez que se entra)
  const [ageOk, setAgeOk] = useState(false);
  const galileoRef = useRef<HTMLAudioElement | null>(null);

  // Red de seguridad: si la Mega Fire está deshabilitada, saca al jugador a la Europea
  // (cubre a quien estuviera dentro de la mesa al desplegarse el bloqueo).
  useEffect(() => {
    if (MEGAFIRE_ENABLED || mode !== 'megafire') return;
    setMode('europea');
    setBets({});
    setFireNumbers([]);
    setLitFire([]);
    setMegaEvent(false);
    setPhase('idle');
    setActiveBonus(null);
    toast({ title: 'Mega Fire Blaze deshabilitada', description: 'La mesa está cerrada temporalmente. Vuelves a la Ruleta Europea.', variant: 'destructive' });
  }, [mode]);

  const secondary = useMemo(() => accounts.filter((a) => (a.accountType || 'secundaria') === 'secundaria'), [accounts]);

  const refreshWallet = async () => {
    const res = await casinoGetWallet();
    if (res.success) setChips(res.chips);
  };

  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      const [accRes] = await Promise.all([getMyBankAccounts(), refreshWallet()]);
      if (accRes.success && accRes.accounts) {
        setAccounts(accRes.accounts);
        const sec = accRes.accounts.filter((a) => (a.accountType || 'secundaria') === 'secundaria');
        if (sec[0]) setAccountId(sec[0].id);
      }
      setPageLoading(false);
    })();
  }, [loading, user]);

  const totalBet = useMemo(() => Object.values(bets).reduce((s, v) => s + v, 0), [bets]);

  const placeBet = (betId: string) => {
    if (spinning) return;
    audio.chip();
    setBets((p) => ({ ...p, [betId]: (p[betId] || 0) + chipValue }));
  };
  const clearBets = () => setBets({});
  const repeatBets = () => { if (!spinning && Object.keys(lastBets).length) setBets({ ...lastBets }); };

  const toApiBets = (): RouletteBet[] => Object.entries(bets).map(([id, amount]) => {
    const ci = id.indexOf(':');
    if (ci === -1) return { kind: id, amount };
    const kind = id.slice(0, ci); const rest = id.slice(ci + 1);
    if (INSIDE_KINDS.includes(kind)) return { kind, numbers: rest.split('-').map(Number), amount };
    return { kind, value: Number(rest), amount };
  });

  const pendingResRef = useRef<any>(null);

  const finishOutcome = (res: any) => {
    setChips(res.chips);
    setLastOutcome({ net: res.net });
    setHistory((h) => [res.number, ...h].slice(0, 25));
    if (res.net > 0) { audio.win(); setFlash('win'); toast({ title: `¡Ganaste ${res.net} fichas!`, description: `Salió el ${res.number}.` }); }
    else { audio.lose(); setFlash('lose'); toast({ title: `Salió el ${res.number}`, description: `Perdiste ${Math.abs(res.net)} fichas.`, variant: 'destructive' }); }
    setTimeout(() => setFlash(null), 2200);
    setBets({});
    setTimeout(() => { setFireNumbers([]); setLitFire([]); setMegaEvent(false); setPhase('idle'); }, 3500);
  };

  // Lanza la animación de la rueda y resuelve el resultado.
  const runWheelAndReveal = (res: any, betsSnapshot: Record<string, number>) => {
    const index = WHEEL_ORDER.indexOf(res.number);
    const targetWithin = (360 - (index + 0.5) * STEP) % 360;
    setRotation((prev) => Math.ceil(prev / 360) * 360 + 6 * 360 + targetWithin);
    setBallRotation((prev) => Math.floor(prev / 360) * 360 - 9 * 360);
    audio.spin();

    setTimeout(() => {
      setResult(res.number);
      setSpinning(false);

      if (mode === 'megafire' && res.isFire && galileoRef.current) {
        try { galileoRef.current.currentTime = 0; galileoRef.current.play().catch(() => {}); } catch {}
      }

      if (mode === 'megafire' && res.bonusTriggered && res.bonus) {
        const betAmount = res.bonusBase || betsSnapshot[`straight:${res.number}`] || 0;
        const bonusPayout = (res.results.find((r: any) => r.bonus)?.payout) || 0;
        // Pequeña pausa para que se vea que cayó en número de fuego, luego la pantalla épica
        setTimeout(() => {
          setActiveBonus({ bonus: res.bonus, betAmount, number: res.number, payout: bonusPayout });
          pendingResRef.current = res;
        }, 1100);
        return;
      }

      finishOutcome(res);
    }, 5100);
  };

  const spin = async () => {
    if (mode === 'megafire' && !MEGAFIRE_ENABLED) { setMode('europea'); return; }
    if (totalBet <= 0) { toast({ title: 'Sin apuestas', description: 'Coloca al menos una ficha.', variant: 'destructive' }); return; }
    if (totalBet > chips) { toast({ title: 'Fichas insuficientes', description: 'Ingresa más fichas en el casino.', variant: 'destructive' }); return; }
    const stake = totalBet;
    const betsSnapshot = { ...bets };
    setLastBets({ ...bets });
    setSpinning(true);
    setResult(null);
    setLastOutcome(null);
    setFlash(null);
    setFireNumbers([]);
    setLitFire([]);
    setMegaEvent(false);
    setChips((c) => c - stake);

    const res: any = mode === 'megafire' ? await casinoSpinMegaFire(toApiBets()) : await casinoSpinRoulette(toApiBets());
    if (!res || !res.success) {
      setChips((c) => c + stake);
      setSpinning(false);
      setPhase('idle');
      // El backend ha cerrado la mesa: sacamos al jugador y recargamos para traer la web al día.
      if (typeof res?.message === 'string' && res.message.includes('MEGAFIRE_DISABLED')) {
        setMode('europea');
        setBets({});
        toast({ title: 'Mesa cerrada', description: 'La Mega Fire Blaze se ha deshabilitado. Recargando…', variant: 'destructive' });
        setTimeout(() => window.location.reload(), 1800);
        return;
      }
      toast({ title: 'Error', description: res?.message || 'No se pudo jugar.', variant: 'destructive' });
      return;
    }

    if (mode === 'megafire' && Array.isArray(res.fireNumbers)) {
      // Fase 1: encender los números de fuego UNO A UNO encima de la ruleta.
      const fns: number[] = res.fireNumbers;
      setFireNumbers(fns);
      setMegaEvent(!!res.mega);
      setPhase('igniting');
      const perNumber = 600;
      fns.forEach((n, i) => {
        setTimeout(() => { setLitFire((prev) => [...prev, n]); audio.ignite(); }, 400 + i * perNumber);
      });
      const ignitionTotal = 400 + fns.length * perNumber + 1000;
      // Fase 2: tras encenderlos todos, empieza el giro.
      setTimeout(() => { setPhase('spinning'); runWheelAndReveal(res, betsSnapshot); }, ignitionTotal);
      return;
    }

    // Ruleta europea: giro directo
    audio.spin();
    const index = WHEEL_ORDER.indexOf(res.number);
    const targetWithin = (360 - (index + 0.5) * STEP) % 360;
    setRotation((prev) => Math.ceil(prev / 360) * 360 + 6 * 360 + targetWithin);
    setBallRotation((prev) => Math.floor(prev / 360) * 360 - 9 * 360);
    setTimeout(() => {
      setResult(res.number);
      setChips(res.chips);
      setLastOutcome({ net: res.net });
      setHistory((h) => [res.number, ...h].slice(0, 25));
      setSpinning(false);
      if (res.net > 0) { audio.win(); setFlash('win'); toast({ title: `¡Ganaste ${res.net} fichas!`, description: `Salió el ${res.number}.` }); }
      else { audio.lose(); setFlash('lose'); toast({ title: `Salió el ${res.number}`, description: `Perdiste ${Math.abs(res.net)} fichas.`, variant: 'destructive' }); }
      setTimeout(() => setFlash(null), 2200);
      setBets({});
    }, 5100);
  };

  const handleBonusComplete = () => {
    setActiveBonus(null);
    if (pendingResRef.current) { finishOutcome(pendingResRef.current); pendingResRef.current = null; }
  };

  if (loading || pageLoading) {
    return <div className="flex h-60 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-400" /></div>;
  }

  if (secondary.length === 0) {
    return (
      <div className="mx-auto max-w-lg rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-950/40 to-black p-10 text-center">
        <Dice5 className="mx-auto mb-4 h-12 w-12 text-amber-400" />
        <h2 className="font-headline text-2xl font-bold">JartiBet</h2>
        <p className="mt-2 text-muted-foreground">Necesitas una cuenta bancaria secundaria para jugar. Abre una en JartiBank.</p>
      </div>
    );
  }

  // Verificación de edad (18+) — aparece cada vez que se entra a JartiBet.
  if (!ageOk) return <AgeGate onAccept={() => setAgeOk(true)} />;

  return (
    <div className="space-y-6 pb-12">
      <audio ref={galileoRef} src="/galileo.mp3" preload="auto" />
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/60 via-black to-red-950/50 p-6 shadow-[0_0_40px_rgba(245,158,11,0.15)]">
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-amber-500/25 blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-red-600/20 blur-3xl" />
        <div className="absolute inset-x-4 top-2 flex justify-between">
          {Array.from({ length: 18 }).map((_, i) => (
            <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-amber-300" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.08 }} />
          ))}
        </div>
        <div className="relative flex flex-wrap items-center justify-between gap-4 pt-2">
          <div className="flex items-center gap-4">
            <div className="grid h-16 w-16 place-items-center rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/30 to-red-600/20 text-amber-300 shadow-[0_0_20px_rgba(245,158,11,0.4)]"><Dice5 className="h-8 w-8" /></div>
            <div>
              <h1 className="font-headline text-4xl font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-b from-amber-200 to-amber-500 drop-shadow">JartiBet</h1>
              <p className="text-sm font-semibold uppercase tracking-widest text-amber-100/60">{mode === 'megafire' ? 'Mega Fire Blaze 🔥' : 'Casino · Ruleta Europea'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl border-2 border-amber-400/40 bg-black/50 px-5 py-2.5 text-right shadow-inner">
              <p className="text-[0.65rem] uppercase tracking-widest text-amber-200/60">Fichas</p>
              <p className="flex items-center gap-1.5 text-2xl font-black text-amber-300"><Coins className="h-5 w-5" />{chips.toLocaleString('es-ES')}</p>
            </div>
            <Button onClick={() => setDepositOpen(true)} className="bg-gradient-to-b from-amber-400 to-amber-600 font-bold text-black hover:from-amber-300 hover:to-amber-500"><Wallet className="mr-1 h-4 w-4" /> Caja</Button>
          </div>
        </div>
      </div>

      {/* Selector de sección: Ruleta / Apuestas */}
      <div className="flex gap-2 rounded-2xl border border-amber-500/20 bg-black/40 p-2">
        <button onClick={() => setSection('ruleta')}
          className={cn('flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all',
            section === 'ruleta' ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black shadow' : 'text-amber-100/70 hover:bg-white/5')}>
          <Dice5 className="h-4 w-4" /> Ruleta
        </button>
        <button onClick={() => setSection('apuestas')}
          className={cn('flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all',
            section === 'apuestas' ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow' : 'text-sky-100/70 hover:bg-white/5')}>
          <Trophy className="h-4 w-4" /> Apuestas
        </button>
      </div>

      {section === 'apuestas' && <SportsBetting chips={chips} onChips={setChips} />}

      {section === 'ruleta' && (<>
      {/* Selector de modo de juego */}
      <div className="flex gap-2 rounded-2xl border border-amber-500/20 bg-black/40 p-2">
        <button onClick={() => !spinning && setMode('europea')} disabled={spinning}
          className={cn('flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all',
            mode === 'europea' ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black shadow' : 'text-amber-100/70 hover:bg-white/5')}>
          <Dice5 className="h-4 w-4" /> Ruleta Europea
        </button>
        <button onClick={() => { if (!MEGAFIRE_ENABLED) { toast({ title: 'Mesa cerrada', description: 'La Mega Fire Blaze está deshabilitada temporalmente.', variant: 'destructive' }); return; } if (!spinning) setMode('megafire'); }}
          disabled={spinning || !MEGAFIRE_ENABLED}
          title={!MEGAFIRE_ENABLED ? 'Mesa cerrada temporalmente' : undefined}
          className={cn('flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-black transition-all',
            !MEGAFIRE_ENABLED ? 'cursor-not-allowed text-zinc-500 line-through decoration-2 opacity-60'
              : mode === 'megafire' ? 'bg-gradient-to-r from-orange-500 via-red-600 to-orange-600 text-white shadow-[0_0_20px_rgba(249,115,22,0.6)]' : 'text-orange-200/70 hover:bg-white/5')}>
          {MEGAFIRE_ENABLED ? <Flame className="h-4 w-4" /> : <Lock className="h-4 w-4 no-underline" />} MEGA FIRE BLAZE
        </button>
      </div>

      {/* Aviso de mesa cerrada */}
      {!MEGAFIRE_ENABLED && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-zinc-500/30 bg-zinc-500/5 px-4 py-2.5 text-xs text-zinc-300">
          <Lock className="h-4 w-4 text-zinc-400" />
          <span>La <b>Mega Fire Blaze</b> está <b className="text-zinc-100">deshabilitada temporalmente</b>. Puedes seguir jugando a la Ruleta Europea.</span>
        </div>
      )}

      {/* Aviso del modo Mega Fire */}
      {MEGAFIRE_ENABLED && mode === 'megafire' && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-orange-500/30 bg-orange-500/5 px-4 py-2.5 text-xs text-orange-100/80">
          <Flame className="h-4 w-4 text-orange-400" />
          <span>Pleno paga <b className="text-orange-300">29:1</b>. Si la bola cae en un <b className="text-orange-300">número fuego</b> y tenías un pleno en él → ronda bonus <b>Hold &amp; Win</b> (hasta <b className="text-amber-300">2.000×</b>, jackpots Mini/Minor/Major/Grand).</span>
        </div>
      )}

      {/* Marcador de últimos 25 números */}
      <div className="flex items-center gap-3 rounded-2xl border border-amber-500/20 bg-black/40 px-4 py-3">
        <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-amber-200/60">Últimos números</span>
        <div className="flex flex-1 flex-wrap gap-1.5">
          {history.length === 0 ? (
            <span className="text-sm text-muted-foreground">Aún no hay jugadas.</span>
          ) : history.map((n, i) => (
            <span key={i} className={cn('grid h-8 w-8 place-items-center rounded-lg text-sm font-black text-white shadow',
              i === 0 && 'ring-2 ring-amber-300',
              numColor(n) === 'green' ? 'bg-green-600' : numColor(n) === 'red' ? 'bg-red-600' : 'bg-zinc-800')}>{n}</span>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        {/* Rueda */}
        {mode === 'megafire' ? (
          <div className="relative space-y-4 overflow-hidden rounded-3xl border-2 border-orange-500/40 bg-[radial-gradient(circle_at_50%_20%,#3f1208,#0a0505_70%)] p-6 shadow-[inset_0_0_70px_rgba(0,0,0,0.7),0_0_30px_rgba(249,115,22,0.2)]">
            {/* Panel de números de fuego ENCIMA de la ruleta */}
            <FireNumbersPanel fireNumbers={fireNumbers} litFire={litFire} mega={megaEvent} igniting={phase === 'igniting'} />
            <MegaFireWheel rotation={rotation} ballRotation={ballRotation} spinning={phase === 'spinning' && spinning} result={result} fireNumbers={litFire} igniting={phase === 'igniting'} />
            {lastOutcome && !spinning && !activeBonus && (
              <motion.p initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className={cn('text-center text-2xl font-black drop-shadow', lastOutcome.net >= 0 ? 'text-green-400' : 'text-red-400')}>
                {lastOutcome.net >= 0 ? `+${lastOutcome.net}` : lastOutcome.net} fichas
              </motion.p>
            )}
          </div>
        ) : (
          <div className="relative space-y-4 overflow-hidden rounded-3xl border-2 border-amber-500/20 bg-[radial-gradient(circle_at_50%_30%,#0c3b22,#031a10_70%)] p-6 shadow-[inset_0_0_60px_rgba(0,0,0,0.6)]">
            <RouletteWheel rotation={rotation} ballRotation={ballRotation} spinning={spinning} result={result} />
            {lastOutcome && !spinning && (
              <motion.p initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className={cn('text-center text-2xl font-black drop-shadow', lastOutcome.net >= 0 ? 'text-green-400' : 'text-red-400')}>
                {lastOutcome.net >= 0 ? `+${lastOutcome.net}` : lastOutcome.net} fichas
              </motion.p>
            )}
          </div>
        )}

        {/* Tablero */}
        <div className="space-y-4 rounded-3xl border-2 border-amber-500/20 bg-emerald-950/40 p-5 shadow-[inset_0_0_50px_rgba(0,0,0,0.5)]">
          {/* Selector de ficha */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold uppercase tracking-wider text-amber-200/70">Ficha</span>
            {CHIPS.map((c) => (
              <button key={c} onClick={() => setChipValue(c)}
                className={cn('rounded-full transition-transform', chipValue === c ? 'scale-110 ring-2 ring-amber-300 ring-offset-2 ring-offset-emerald-950' : 'opacity-80 hover:scale-105')}>
                <PokerChip value={c} size={46} color={CHIP_COLORS[c]} />
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-amber-100/80">Apostado: <b className="text-amber-300">{totalBet}</b></span>
              <Button variant="outline" size="sm" onClick={repeatBets} disabled={spinning || Object.keys(lastBets).length === 0}><Repeat className="mr-1 h-4 w-4" /> Repetir</Button>
              <Button variant="outline" size="sm" onClick={clearBets} disabled={spinning || totalBet === 0}><RotateCcw className="mr-1 h-4 w-4" /> Limpiar</Button>
            </div>
          </div>

          {/* Tablero de números (con apuestas internas) + externas */}
          <div className="overflow-x-auto rounded-xl border border-amber-500/20 bg-emerald-900/20 p-3">
            <div className="space-y-1.5" style={{ width: BOARD_W }}>
              <NumbersBoard bets={bets} placeBet={placeBet} fireNumbers={mode === 'megafire' ? fireNumbers : []} />

              {/* Docenas */}
              <div className="grid gap-1" style={{ gridTemplateColumns: `${X0}px repeat(3, 1fr) ${COLW}px` }}>
                <div />
                {[1, 2, 3].map((d) => (
                  <OutsideCell key={d} label={d === 1 ? '1ª DOCENA' : d === 2 ? '2ª DOCENA' : '3ª DOCENA'} amount={bets[`dozen:${d}`] || 0} onClick={() => placeBet(`dozen:${d}`)} className="h-10 rounded" />
                ))}
                <div />
              </div>

              {/* Apuestas externas */}
              <div className="grid gap-1" style={{ gridTemplateColumns: `${X0}px repeat(6, 1fr) ${COLW}px` }}>
                <div />
                <OutsideCell label="1-18" amount={bets['low'] || 0} onClick={() => placeBet('low')} className="h-10 rounded" />
                <OutsideCell label="PAR" amount={bets['even'] || 0} onClick={() => placeBet('even')} className="h-10 rounded" />
                <OutsideCell label={<span className="block h-5 w-5 rotate-45 rounded-sm bg-gradient-to-br from-red-500 to-red-700 shadow" />} color="none" amount={bets['red'] || 0} onClick={() => placeBet('red')} className="h-10 rounded" />
                <OutsideCell label={<span className="block h-5 w-5 rotate-45 rounded-sm bg-gradient-to-br from-zinc-700 to-black border border-white/30 shadow" />} color="none" amount={bets['black'] || 0} onClick={() => placeBet('black')} className="h-10 rounded" />
                <OutsideCell label="IMPAR" amount={bets['odd'] || 0} onClick={() => placeBet('odd')} className="h-10 rounded" />
                <OutsideCell label="19-36" amount={bets['high'] || 0} onClick={() => placeBet('high')} className="h-10 rounded" />
                <div />
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Coloca la ficha sobre un número (pleno {mode === 'megafire' ? '29:1' : '35:1'}), o en las líneas y cruces entre números: 2 números 17:1, calle 11:1, esquina 8:1, línea 5:1.
          </p>

          <Button onClick={spin} disabled={spinning || totalBet === 0}
            className={cn('h-14 w-full text-lg font-black tracking-wide text-black hover:brightness-110',
              mode === 'megafire'
                ? 'bg-gradient-to-r from-orange-500 via-red-600 to-orange-500 text-white shadow-[0_0_28px_rgba(249,115,22,0.6)]'
                : 'bg-gradient-to-r from-amber-500 via-amber-400 to-red-600 shadow-[0_0_24px_rgba(245,158,11,0.4)]')}>
            {spinning ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> GIRANDO...</>
              : mode === 'megafire' ? <><Flame className="mr-2 h-5 w-5" /> GIRAR · MEGA FIRE ({totalBet})</>
              : <><Trophy className="mr-2 h-5 w-5" /> GIRAR ({totalBet} fichas)</>}
          </Button>
        </div>
      </div>
      </>)}

      <CajaDialog open={depositOpen} onClose={() => setDepositOpen(false)} accounts={secondary} accountId={accountId} setAccountId={setAccountId} chips={chips} onDone={(c) => setChips(c)} />

      {/* Bonus Hold & Win (Mega Fire Blaze) */}
      {activeBonus && (
        <FireBonusOverlay
          bonus={activeBonus.bonus}
          betAmount={activeBonus.betAmount}
          winningNumber={activeBonus.number}
          payout={activeBonus.payout}
          onComplete={handleBonusComplete}
        />
      )}

      {/* Animación de ganar / perder */}
      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="pointer-events-none fixed inset-0 z-[80] flex items-center justify-center"
          >
            <div className={cn('absolute inset-0', flash === 'win'
              ? 'bg-[radial-gradient(circle_at_center,rgba(250,204,21,0.35),transparent_65%)]'
              : 'bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.3),transparent_65%)]')} />
            {flash === 'win' ? (
              <>
                {/* Monedas cayendo */}
                {Array.from({ length: 22 }).map((_, i) => (
                  <motion.span key={i} className="absolute text-2xl"
                    initial={{ y: -60, x: (i - 11) * 38, opacity: 0, rotate: 0 }}
                    animate={{ y: 420, opacity: [0, 1, 1, 0], rotate: 360 }}
                    transition={{ duration: 1.6, delay: i * 0.04, ease: 'easeIn' }}>🪙</motion.span>
                ))}
                <motion.div initial={{ scale: 0, rotate: -12 }} animate={{ scale: [0, 1.2, 1], rotate: 0 }} transition={{ duration: 0.6 }}
                  className="relative rounded-3xl border-2 border-amber-300 bg-black/70 px-10 py-5 text-center shadow-[0_0_50px_rgba(250,204,21,0.6)]">
                  <p className="font-headline text-4xl font-black text-amber-300">¡GANASTE!</p>
                  {lastOutcome && <p className="text-2xl font-black text-green-400">+{lastOutcome.net} fichas</p>}
                </motion.div>
              </>
            ) : (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="relative rounded-3xl border-2 border-red-500/60 bg-black/70 px-10 py-5 text-center shadow-[0_0_40px_rgba(239,68,68,0.5)]">
                <p className="font-headline text-3xl font-black text-red-400">Suerte la próxima</p>
                {lastOutcome && <p className="text-xl font-bold text-red-300">{lastOutcome.net} fichas</p>}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CajaDialog({ open, onClose, accounts, accountId, setAccountId, chips, onDone }: {
  open: boolean; onClose: () => void; accounts: BankAccount[]; accountId: string; setAccountId: (id: string) => void; chips: number; onDone: (chips: number) => void;
}) {
  const { toast } = useToast();
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const selected = accounts.find((a) => a.id === accountId);
  const oro = selected ? (selected.oro ?? (selected as any).balance ?? 0) : 0;

  const doOp = async (kind: 'deposit' | 'withdraw') => {
    const amt = Math.floor(Number(amount));
    if (!accountId || !amt || amt <= 0) { toast({ title: 'Importe no válido', variant: 'destructive' }); return; }
    setBusy(true);
    const res = kind === 'deposit' ? await casinoDeposit(accountId, amt) : await casinoWithdraw(accountId, amt);
    if (res.success) { toast({ title: 'Hecho', description: res.message }); onDone(res.chips); setAmount(''); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Caja de JartiBet</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Cuenta secundaria</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger><SelectValue placeholder="Elige una cuenta" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.accountName} — {(a.oro ?? (a as any).balance ?? 0).toLocaleString('es-ES')} oro</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Saldo cuenta: {oro.toLocaleString('es-ES')} oro · Fichas casino: {chips.toLocaleString('es-ES')}</p>
          </div>
          <div className="space-y-1.5">
            <Label>Importe (1 ficha = 1 oro)</Label>
            <Input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => doOp('deposit')} disabled={busy} className="bg-green-600 hover:bg-green-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownToLine className="mr-1 h-4 w-4" />} Ingresar
            </Button>
            <Button onClick={() => doOp('withdraw')} disabled={busy} variant="outline">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpFromLine className="mr-1 h-4 w-4" />} Retirar
            </Button>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cerrar</Button></DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
